import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAvailableFilterState } from './StateProvider';
import { useAddStartFilterMetric } from '../../../Shared/useAddStartFilterMetric';
import {
  ActionsContextType,
  ConnectionThresholdAction,
  UseFilterActionsReturn,
  ActionsProviderProps,
  FilterTransportType,
  ConnectionThresholdStatePerTransportType,
  ConnectionThresholdsPerTransportType,
} from './types';
import { useTransportTypeProduct } from '../../TransportTypeFilter/Provider';

export const ActionsContext = createContext<ActionsContextType>(() => {});

const resetReducer = (
  state: ConnectionThresholdStatePerTransportType,
  {
    availableConnectionThresholds,
  }: { availableConnectionThresholds: ConnectionThresholdsPerTransportType }
): ConnectionThresholdStatePerTransportType => {
  return {
    '': availableConnectionThresholds[''].map(({ resetValue }) => resetValue),
    TRAIN: availableConnectionThresholds['TRAIN'].map(
      ({ resetValue }) => resetValue
    ),
    PLANE: availableConnectionThresholds['PLANE'].map(
      ({ resetValue }) => resetValue
    ),
  };
};

const legSelectReducer = (
  state: ConnectionThresholdStatePerTransportType,
  {
    availableConnectionThresholds,
    value,
    legId,
    transportType,
  }: {
    availableConnectionThresholds: ConnectionThresholdsPerTransportType;
    value: number;
    legId: number;
    transportType: FilterTransportType;
  }
): ConnectionThresholdStatePerTransportType => {
  const connectionThresholds = state || {
    '': availableConnectionThresholds[''].map(
      ({ defaultValue }) => defaultValue
    ),
    TRAIN: availableConnectionThresholds['TRAIN'].map(
      ({ defaultValue }) => defaultValue
    ),
    PLANE: availableConnectionThresholds['PLANE'].map(
      ({ defaultValue }) => defaultValue
    ),
  };

  const updatedConnectionThresholds = (
    connectionThresholds[transportType] ?? []
  ).map((connectionThreshold, index) => {
    return legId !== index ? connectionThreshold : value;
  });

  return {
    ...connectionThresholds,
    [transportType]: updatedConnectionThresholds,
  };
};

const legResetReducer = (
  state: ConnectionThresholdStatePerTransportType,
  {
    availableConnectionThresholds,
    legId,
    transportType,
  }: {
    availableConnectionThresholds: ConnectionThresholdsPerTransportType;
    legId: number;
    transportType: FilterTransportType;
  }
): ConnectionThresholdStatePerTransportType =>
  legSelectReducer(state, {
    legId,
    value: availableConnectionThresholds[transportType][legId]?.resetValue,
    availableConnectionThresholds,
    transportType,
  });

export const reducer = (
  connectionThresholds: ConnectionThresholdStatePerTransportType,
  action: ConnectionThresholdAction
): ConnectionThresholdStatePerTransportType => {
  switch (action.type) {
    case 'INIT':
      return null;
    case 'LEG_SELECT':
      return legSelectReducer(connectionThresholds, action);
    case 'LEG_RESET':
      return legResetReducer(connectionThresholds, action);
    case 'RESET_ALL':
      return resetReducer(connectionThresholds, action);
    default:
      return connectionThresholds;
  }
};

export const useLegFilterActions = (
  legId: number,
  transportType: FilterTransportType
) => {
  const addStartMetric = useAddStartFilterMetric();
  const availableConnectionThresholds = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);

  return useMemo(() => {
    return {
      select: addStartMetric((value: number) => {
        dispatch({
          type: 'LEG_SELECT',
          transportType,
          legId,
          value,
          availableConnectionThresholds,
        });
      }),
    };
  }, [
    addStartMetric,
    dispatch,
    transportType,
    legId,
    availableConnectionThresholds,
  ]);
};

export const useFilterActions = (): UseFilterActionsReturn => {
  const availableConnectionThresholds = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);
  const addStartMetric = useAddStartFilterMetric();
  const { product } = useTransportTypeProduct();

  return useMemo(() => {
    return {
      resetAll: addStartMetric(() => {
        dispatch({
          type: 'RESET_ALL',
          availableConnectionThresholds,
        });
      }),
      resetLeg: addStartMetric(({ legId }: { legId: number }) => {
        dispatch({
          type: 'LEG_RESET',
          legId,
          availableConnectionThresholds,
          transportType: product as FilterTransportType,
        });
      }),
    };
  }, [addStartMetric, dispatch, availableConnectionThresholds, product]);
};

const ActionsProvider: React.FC<ActionsProviderProps> = ({
  dispatch,
  children,
}) => {
  const availableConnectionThresholds = useAvailableFilterState();
  useEffect(() => {
    if (!availableConnectionThresholds) {
      dispatch({ type: 'INIT' });
    }
  }, [dispatch, availableConnectionThresholds]);
  return (
    <ActionsContext.Provider value={dispatch}>
      {children}
    </ActionsContext.Provider>
  );
};

export default ActionsProvider;
